/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { getDeep } from '@shared/utils';
import { getDefaultPieveAppSettings } from '@/lib/pieveAppSettings.js';
import normalizeProjectId from '@/lib/normalizeProjectId.js';
import { EMPTY_ARRAY, EMPTY_OBJECT, HOME_PROJECT_ID } from '@/redux/selectors/pieveSelectors/constants.js';
import { THREAD_DEFAULTS_PROJECT_ID_PREF_KEY } from '@/lib/appConstants.js';
import { pref as selectPref, prefs as selectPrefs } from '@/redux/selectors/uiSelectors.js';

const DEFAULT_PIEVE_SETTINGS = getDefaultPieveAppSettings();

export const storedProjects = (state) => state?.pieve?.projects || EMPTY_ARRAY;

export const projectsById = createSelector([storedProjects], (projects) => {
  if (!projects.length) {
    return EMPTY_OBJECT;
  }

  const map = {};

  for (const project of projects) {
    const projectId = normalizeProjectId(project?.id);

    if (projectId) {
      map[projectId] = project;
    }
  }

  return map;
});

export const projectItems = createSelector([storedProjects], (projects) => {
  if (!projects.length) {
    return EMPTY_ARRAY;
  }

  return [...projects].sort((left, right) => {
    return String(left?.name || left?.id || '').localeCompare(String(right?.name || right?.id || ''));
  });
});

export const activeProjectId = createSelector([storedProjects], (projects) => {
  return normalizeProjectId((projects || []).find((project) => project?.isDefault === true)?.id);
});

export { isDraftThreadActive } from '@/redux/selectors/pieveSelectors/activeThreadIdSelector.js';

export const allowProjectFolders = createSelector([selectPrefs], (prefs) => {
  return Boolean(getDeep('pieve.settings.allowProjectFolders', prefs));
});

export const sidebarThreadSortByPreference = createSelector([selectPrefs], (prefs) => {
  const value = String(getDeep('pieve.settings.threadSortBy', prefs) || '')
    .trim()
    .toLowerCase();
  return value === 'created' || value === 'updated' ? value : DEFAULT_PIEVE_SETTINGS.threadSortBy;
});

export const sidebarThreadShowModePreference = createSelector([selectPrefs], (prefs) => {
  const value = String(getDeep('pieve.settings.threadShowMode', prefs) || '')
    .trim()
    .toLowerCase();
  return value === 'relevant' ? 'relevant' : DEFAULT_PIEVE_SETTINGS.threadShowMode;
});

export const visibleActiveProjectId = createSelector([activeProjectId, allowProjectFolders], (activeProjectIdValue, allowProjectFoldersValue) => {
  const normalizedProjectId = normalizeProjectId(activeProjectIdValue);

  if (!allowProjectFoldersValue) {
    return HOME_PROJECT_ID;
  }

  return normalizedProjectId || HOME_PROJECT_ID;
});

const selectPendingNewThreadProjectIdFromPrefs = (state) => selectPref(state, THREAD_DEFAULTS_PROJECT_ID_PREF_KEY) || null;

export const visiblePendingNewThreadProjectId = createSelector([selectPendingNewThreadProjectIdFromPrefs, allowProjectFolders], (pendingProjectId, allowProjectFoldersValue) => {
  const normalizedProjectId = normalizeProjectId(pendingProjectId);

  if (!normalizedProjectId) {
    return null;
  }

  if (!allowProjectFoldersValue) {
    return HOME_PROJECT_ID;
  }

  return normalizedProjectId;
});

export const resolvedMainThreadDraftProjectId = createSelector([visiblePendingNewThreadProjectId, visibleActiveProjectId], (pendingProjectId, activeProjectIdValue) => {
  return pendingProjectId || activeProjectIdValue || HOME_PROJECT_ID;
});

export const projectMetaById = projectsById;
